import { ArrowCcwIcon, CircleInfoIcon, SettingsIcon } from "@xola/icons";
import { Alert, flash, Popover, SubmitButton, Switch } from "@xola/ui-kit";
import { isEqual } from "lodash";
import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useParams } from "react-router-dom";
import { Guide, GuidePermission } from "types";
import { api } from "../../../../api/api";
import { useSeller } from "../../../../hooks/useAccount";
import { useInvalidate } from "../../../../hooks/useInvalidation";
import { useResource } from "../../../../hooks/useResource";
import { ConfirmLeaveModal } from "../../../Common/ConfirmLeaveModal";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { TimePickerPopover } from "../../../Common/TimePickerPopover";
import { ContentLayout } from "../../../Layout/ContentLayout";

const defaultValues: GuidePermission = {
    canModifySchedules: false,
    modifySchedulesOffset: 30,
};

export const PermissionsTab = () => {
    const seller = useSeller();
    const { id } = useParams<{ id?: ID }>();
    const { data: guide } = useResource<Guide>(["sellers", seller.id, "guides", id]);
    const [guidePermission, setGuidePermission] = useState(guide?.guidePermission ?? null);
    const invalidate = useInvalidate();

    const { mutateAsync: updatePermissions, isLoading: isUpdating } = useMutation(async () => {
        if (guidePermission) {
            return api.post(`/sellers/${seller.id}/guides/${guide?.id}/preferences/guidePermission`, guidePermission);
        }

        return api.delete(`/sellers/${seller.id}/guides/${guide?.id}/preferences/guidePermission`);
    });

    useEffect(() => {
        setGuidePermission(guide?.guidePermission ?? null);
    }, [setGuidePermission, guide?.guidePermission]);

    const handleApplyOffset = (modifySchedulesOffset: number) => {
        setGuidePermission({ ...guidePermission!, modifySchedulesOffset });
    };

    const handleCancelClick = () => {
        setGuidePermission(guide?.guidePermission ?? null);
    };

    const handleSave = async () => {
        await updatePermissions();
        invalidate("guides", guide!.id);
        flash.success("Your settings are saved");
    };

    const isOverridden = !!guidePermission;

    return (
        <ContentLayout className="space-y-8" isDisabled={isUpdating}>
            <ConfirmLeaveModal isFormTouched={!isEqual(guide?.guidePermission ?? null, guidePermission)} />

            <h3>Permissions</h3>

            <Alert color={isOverridden ? "warning" : "primary"} className="mb-12 flex-row py-6 px-6">
                <div className="flex justify-center align-middle">
                    <div className="mr-2 flex-1 self-center">
                        {isOverridden
                            ? "You have modified Global Permissions for this guide."
                            : "Global Permissions are assigned to this guide. You can override permissions below."}
                    </div>

                    <SecondaryButton
                        icon={isOverridden ? <ArrowCcwIcon /> : <SettingsIcon />}
                        className="self-center align-bottom"
                        size="small"
                        onClick={() => {
                            setGuidePermission(isOverridden ? null : defaultValues);
                        }}
                    >
                        {isOverridden ? "Restore Global Permissions" : "Edit Global Permissions"}
                    </SecondaryButton>
                </div>
            </Alert>

            {isOverridden ? (
                <Switch.Group>
                    <Switch
                        data-testid="permissions-tab-modify-schedules-toggle"
                        isChecked={guidePermission?.canModifySchedules}
                        size="medium"
                        onChange={(canModifySchedules: boolean) => {
                            setGuidePermission({ ...guidePermission, canModifySchedules });
                        }}
                    />
                    <Switch.Label direction="right">
                        <h4 className="font-bold">Allow guides to create, update or delete schedules</h4>
                    </Switch.Label>
                </Switch.Group>
            ) : null}

            {guidePermission?.canModifySchedules ? (
                <div className="text-gray-darker" data-testid="permissions-tab-modify-schedules-toggle-text">
                    Prevent guides from making schedule changes affecting their availability within
                    <TimePickerPopover
                        value={guidePermission?.modifySchedulesOffset ?? 1}
                        onApply={handleApplyOffset}
                    />
                    in advance
                    <Popover distance={10} duration={[300, 250]} skidding={50}>
                        <CircleInfoIcon className="ml-2" />
                        <Popover.Content className="space-y-2 p-4">
                            <p className="p2 font-bold">For example:</p>
                            <p className="p2">
                                If you enter &rdquo;1 Week&rdquo;, the guide will not be able to make any changes to
                                their schedule for the coming 7 days, calculated starting with the day that the
                                attempted change is being made.
                            </p>
                            <p className="p2">
                                They could however make a change that affects their schedule 8 days from the current
                                date.
                            </p>
                        </Popover.Content>
                    </Popover>
                </div>
            ) : null}

            <ContentLayout.Footer>
                <SubmitButton isLoading={isUpdating} onClick={handleSave}>
                    Save
                </SubmitButton>

                <SecondaryButton type="reset" onClick={handleCancelClick}>
                    Cancel
                </SecondaryButton>
            </ContentLayout.Footer>
        </ContentLayout>
    );
};
